/**
 * Convierte FormData a objeto JSON
 *
 * Esta función procesa un objeto FormData y lo convierte a un objeto JSON plano,
 * manejando automáticamente los valores de checkbox y campos duplicados.
 *
 * Características:
 * - Convierte automáticamente valores "on" (checkboxes marcados) a booleano true
 * - Maneja campos duplicados convirtiéndolos en arrays
 * - Permite especificar checkboxes que deben añadirse como false si no están presentes
 *
 * @param {FormData} formData - El objeto FormData a convertir
 * @param {Array<string>} checkboxFields - Array opcional con nombres de campos checkbox
 *                                         que deben añadirse como false si no están presentes
 * @returns {Object} Objeto JSON con los datos del formulario
 *
 * @example
 * // Uso básico - convierte "on" a true automáticamente
 * const jsonData = formDataToJson(formData);
 *
 * @example
 * // Con checkboxes específicos - añade campos faltantes como false
 * const jsonData = formDataToJson(formData, ['enabled', 'has-alerts']);
 *
 * @example
 * // Resultado con campo duplicado
 * // FormData: [['tags', 'red'], ['tags', 'blue'], ['active', 'on']]
 * // Resultado: { tags: ['red', 'blue'], active: true }
 */

export function formDataToJson(formData, checkboxFields = []
) {
    const json = {};

    for (const [key, value] of formData.entries()) {
        if (json.hasOwnProperty(key)) {
            // Si ya existe la clave, convertir a array
            if (Array.isArray(json[key])) {
                json[key].push(value);
            } else {
                json[key] = [json[key], value];
            }
        } else {
            json[key] = value === "on" ? true : value
        }
    }
    if (checkboxFields.length > 0) {
        // Si se especifican checkboxes, añadir los que faltan como false
        checkboxFields.forEach(fieldName => {
            if (!json[fieldName]) {
                json[fieldName] = false;
            }
        });
    }

    return json;
}

/**
 * Convierte los datos de un formulario (referencia) a un objeto JSON
 * @param {HTMLFormElement} formRef - Referencia al elemento form
 * @returns {Object} Objeto JSON con los datos del formulario
 */
export function formRefToJson(formRef) {
    if (!formRef || !(formRef instanceof HTMLFormElement)) {
        throw new Error('Se requiere una referencia válida a un elemento form');
    }

    const json = {};
    const formData = new FormData(formRef);

    // Obtener todos los elementos del formulario
    const elements = formRef.elements;

    // Manejar checkboxes no marcados
    for (let i = 0; i < elements.length; i++) {
        const element = elements[i];

        if (element.type === 'checkbox' && !element.checked && element.name) {
            // Solo agregar si no existe ya en formData
            if (!formData.has(element.name)) {
                json[element.name] = false;
            }
        }
    }

    // Procesar datos del FormData
    for (const [key, value] of formData.entries()) {
        if (json.hasOwnProperty(key)) {
            // Si ya existe la clave, convertir a array
            if (Array.isArray(json[key])) {
                json[key].push(value);
            } else {
                json[key] = [json[key], value];
            }
        } else {
            // Manejar checkboxes marcados
            const element = formRef.elements[key];
            if (element && element.type === 'checkbox') {
                json[key] = element.checked;
            } else {
                json[key] = value;
            }
        }
    }

    return json;
}

/**
 * Ejemplo de uso:
 *
 * // Usando formDataToJson:
 * const formData = new FormData();
 * formData.append('nombre', 'Juan');
 * formData.append('email', 'juan@email.com');
 * formData.append('hobbies', 'leer');
 * formData.append('hobbies', 'nadar');
 * const json1 = formDataToJson(formData);
 * // Resultado: { nombre: 'Juan', email: 'juan@email.com', hobbies: ['leer', 'nadar'] }
 *
 * // Usando formRefToJson:
 * const formElement = document.getElementById('miFormulario');
 * const json2 = formRefToJson(formElement);
 * // Resultado: objeto JSON con todos los campos del formulario
 *
 * // Con archivos:
 * const formConArchivos = document.getElementById('formArchivos');
 * const jsonConArchivos = formRefToJson(formConArchivos);
 * // Los archivos se mantienen como objetos File
 */